import React, { JSX } from 'react';
import type { ScannerEntryMode } from './types/ScannerTypes';

type Props = {
  mode: ScannerEntryMode;
  onChange: (mode: ScannerEntryMode) => void;
  /** Disables the entire toggle (e.g. while a parent flow is busy). */
  disabled?: boolean;
  /**
   * Reflected in the Scan button's title/aria attributes so users understand
   * why a scan won't fire yet, but the toggle is always clickable so users
   * can switch modes regardless of scanner connectivity.
   */
  connected?: boolean;
  className?: string;
  size?: 'sm' | 'md';
  /** Inside a composite search bar — no outer pill border/shadow. */
  embedded?: boolean;
};

const BASE =
  'inline-flex items-center select-none transition-colors disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300';

/**
 * Tiny inline pill that lets a user switch a scanner-bound field between
 * serial-scanner entry and free-form manual entry. Stateless; the caller
 * owns the mode state. The toggle never blocks itself when the scanner is
 * disconnected so users can always return to scan mode and let the scanner
 * reconnect.
 */
export default function ScannerEntryModeToggle(props: Props): JSX.Element {
  const {
    mode,
    onChange,
    disabled = false,
    connected = false,
    className = '',
    size = 'sm',
    embedded = false,
  } = props;

  const paddingCls = size === 'md' ? 'px-2.5 py-1 text-[11px]' : 'px-2 py-0.5 text-[10px]';

  return (
    <div
      className={[
        'inline-flex items-center overflow-hidden leading-none',
        embedded
          ? 'rounded-md border-0 bg-transparent shadow-none'
          : 'rounded-full border border-gray-300 bg-white shadow-sm dark:border-slate-600 dark:bg-slate-900',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="group"
      aria-label="Field entry mode"
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (mode !== 'scan') onChange('scan');
        }}
        className={`${BASE} ${paddingCls} uppercase tracking-wide font-semibold ${
          mode === 'scan' && connected
            ? 'bg-emerald-500 text-white'
            : mode === 'scan'
              ? 'bg-amber-400 text-white animate-pulse'
              : 'text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
        }`}
        aria-pressed={mode === 'scan'}
        title={
          mode === 'scan'
            ? connected
              ? 'Scanner connected'
              : 'Waiting for scanner connection…'
            : 'Switch to scanner entry'
        }
      >
        Scan
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (mode !== 'manual') onChange('manual');
        }}
        className={`${BASE} ${paddingCls} uppercase tracking-wide font-semibold border-l border-gray-300 dark:border-slate-600 ${
          mode === 'manual'
            ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-100'
            : 'text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
        }`}
        aria-pressed={mode === 'manual'}
        title="Type a value manually"
      >
        Manual
      </button>
    </div>
  );
}
